<!-- src/views/admin/UserBlockedProviderTable.vue -->
<template>
  <v-card elevation="2" outlined shaped tile class="my-4 px-2">
    <v-card-title>
      User Blocked Providers
      <v-spacer />
      <v-btn color="primary" small @click="openAddDialog()">Add</v-btn>
    </v-card-title>

    <v-card-text>
      <v-row class="align-center">
        <v-col cols="12" md="6">
          <v-text-field
            v-model="searchInput"
            label="Search by user ID, provider ID, or reason"
            dense
            outlined
            clearable
          />
        </v-col>
        <v-col cols="12" md="6" class="text-md-right">
          <span class="grey--text">Total {{ filteredBlocks.length }} items</span>
        </v-col>
      </v-row>

      <v-data-table
        class="elevation-1"
        :headers="headers"
        :items="filteredBlocks"
        item-value="id"
        hide-default-footer
        :loading="loading"
        disable-pagination
      >
        <template #[`item.reason`]="{ item }">
          <span class="truncate-2" :title="item.reason || '-'">{{ item.reason || '-' }}</span>
        </template>

        <template #[`item.created_at`]="{ item }">
          {{ item.created_at }}
        </template>

        <template #[`item.actions`]="{ item }">
          <v-btn small text color="red" :loading="item.__deleting === true" @click="deleteRow(item)"> Delete </v-btn>
        </template>

        <template #no-data>
          <div class="py-6 grey--text text--darken-1 text-center">No records</div>
        </template>
      </v-data-table>
    </v-card-text>

    <!-- Add dialog -->
    <v-dialog v-model="dialog" max-width="520px" scrollable>
      <v-card>
        <v-card-title>
          Add Blocked Provider
          <v-spacer />
          <v-btn icon @click="closeAddDialog"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>

        <v-form ref="formRef" v-model="formValid" lazy-validation>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.user_id"
                  label="User ID"
                  type="number"
                  dense
                  outlined
                  :rules="[requiredRule, numberRule]"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.provider_profile_id"
                  label="Provider Profile ID"
                  type="number"
                  dense
                  outlined
                  :rules="[requiredRule, numberRule]"
                />
              </v-col>

              <v-col cols="12">
                <v-text-field v-model="form.reason" label="Reason (optional)" dense outlined />
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn text @click="closeAddDialog">Cancel</v-btn>
            <v-btn color="primary" text :loading="submitting" @click="submitAdd">Save</v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { Vue } from 'vue-property-decorator';
import dateUtil from '../../utils/dateTime';
import UserBlockedProviderService from '../../services/userBlockedProviders';

export default Vue.extend({
  name: 'UserBlockedProviderTable',
  data: () => ({
    loading: false,
    blocks: [],
    searchInput: '',
    searchTerm: '',
    dialog: false,
    formValid: false,
    submitting: false,
    form: {
      user_id: null,
      provider_profile_id: null,
      reason: '',
    },
    requiredRule: (v) => !!v || 'Required',
    numberRule: (v) => (v !== null && v !== '' && !isNaN(Number(v))) || 'Must be a number',
    headers: [
      { text: 'ID', value: 'id', align: 'center', width: 80 },
      { text: 'User ID', value: 'user_id', align: 'center' },
      { text: 'Provider ID', value: 'provider_profile_id', align: 'center' },
      { text: 'Reason', value: 'reason', align: 'left', width: '35%' },
      { text: 'Created', value: 'created_at', align: 'center', width: 180 },
      { text: 'Actions', value: 'actions', align: 'center', sortable: false, width: 120 },
    ],
  }),
  computed: {
    filteredBlocks() {
      if (!this.searchTerm) return this.blocks;
      const s = this.searchTerm.toLowerCase().trim();
      return this.blocks.filter((b) => {
        const id = String(b.id);
        const uid = String(b.user_id);
        const pid = String(b.provider_profile_id);
        const reason = (b.reason || '').toLowerCase();
        return id.includes(s) || uid.includes(s) || pid.includes(s) || reason.includes(s);
      });
    },
  },
  watch: {
    searchInput() {
      this.debouncedSearch();
    },
  },
  created() {
    this.debouncedSearch = this.debounce(() => {
      this.searchTerm = this.searchInput;
    }, 350);
  },
  mounted() {
    this.fetchBlocks();
  },
  methods: {
    debounce(fn, delay) {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), delay);
      };
    },
    formatDatetime(d) {
      if (!d) return '-';
      try {
        const dt = new Date(d);
        if (isNaN(dt.getTime())) return d;
        return dt.toLocaleString();
      } catch (error) {
        return d;
      }
    },

    async fetchBlocks() {
      try {
        this.loading = true;
        const data = await UserBlockedProviderService.getAll();
        this.blocks = data.map((r) => ({
          id: r.id,
          user_id: r.user_id,
          reason: r.reason || '',
          provider_profile_id: r.provider_profile_id,
          created_at: dateUtil.formateDateTimeForDisplay(r.created_at, 'DD MMM YYYY HH:mm'),
        }));

        console.log('🐬 ~ Log from file: UserBlockedProvider.vue:171 ~ this.blocks:', this.blocks);
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },

    openAddDialog() {
      this.dialog = true;
      this.resetForm();
      this.$nextTick(() => {
        if (this.$refs.formRef.resetValidation) this.$refs.formRef.resetValidation();
      });
    },
    closeAddDialog() {
      this.dialog = false;
    },
    resetForm() {
      this.form = {
        user_id: null,
        provider_profile_id: null,
        reason: '',
      };
    },
    async submitAdd() {
      const valid = this.$refs.formRef.validate ? this.$refs.formRef.validate() : true;
      if (!valid) return;

      try {
        this.submitting = true;
        await UserBlockedProviderService.create({
          user_id: Number(this.form.user_id),
          provider_profile_id: Number(this.form.provider_profile_id),
          reason: this.form.reason.trim() || null,
        });
        this.closeAddDialog();
        await this.fetchBlocks();
        this.$emit('onFetch');
        this.$notify({
          title: 'Success',
          text: 'Blocked provider added.',
          type: 'success',
        });
      } catch (e) {
        this.$notify({
          title: 'Error',
          text: e.response.data.error.message || 'Failed to add blocked provider.',
          type: 'error',
        });
      } finally {
        this.submitting = false;
      }
    },
    async deleteRow(item) {
      try {
        this.$set(item, '__deleting', true);
        await UserBlockedProviderService.delete(item.id);
        await this.fetchBlocks();
        this.$emit('onFetch');
        this.$notify({
          title: 'Success',
          text: 'Blocked provider removed.',
          type: 'success',
        });
      } catch (e) {
        console.error(e);
        this.$notify({
          title: 'Error',
          text: 'Failed to delete blocked provider.',
          type: 'error',
        });
      } finally {
        this.$set(item, '__deleting', false);
      }
    },
  },
});
</script>

<style scoped>
.truncate-2 {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
